import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _class;
import React, { createContext, useCallback, useState } from 'react';
import { SearchDialogAnalyticsContext } from '../../common/analytics';
import { useFilter, addFilters as addFiltersAction, updateFilterState, replaceAllFilters as replaceAllFiltersAction } from '../../common/filters/use-filters';
import { FilterOptionSource } from '../../common/filters/types';
import { messages } from '../../messages';
import { getDateXDaysBack } from '../utils/jira-search-dialog-functions';
const BROCCOLI_30_EXPERIMENT = {
  experimentId: 'broccoli-30-frontend',
  days: 30
};
const BROCCOLI_60_EXPERIMENT = {
  experimentId: 'broccoli-60-frontend',
  days: 60
};
const BROCCOLI_90_EXPERIMENT = {
  experimentId: 'broccoli-90-frontend',
  days: 90
};
export const BROCCOLI_EXPERIMENTS = [BROCCOLI_30_EXPERIMENT, BROCCOLI_60_EXPERIMENT, BROCCOLI_90_EXPERIMENT];
export class BinaryStatusCategory {
  constructor(id, jqlValue) {
    this.id = id;
    this.jqlValue = jqlValue;
  }
}
_class = BinaryStatusCategory;
_defineProperty(BinaryStatusCategory, "OPEN", new _class('open', '"undefined", "In Progress", "To Do"'));
_defineProperty(BinaryStatusCategory, "DONE", new _class('done', '"Done"'));
export const DEFAULT_BINARY_STATUS_CATEGORY_FILTERS = [{
  title: messages.binary_status_category_filter_option_open,
  id: BinaryStatusCategory.OPEN.id,
  isChecked: false,
  isVisible: true,
  filterSource: FilterOptionSource.STATIC
}, {
  title: messages.binary_status_category_filter_option_done,
  id: BinaryStatusCategory.DONE.id,
  isChecked: false,
  isVisible: true,
  filterSource: FilterOptionSource.STATIC
}];
export const useFilters = (defaultFilters = []) => {
  const {
    filters,
    dispatch
  } = useFilter(defaultFilters);
  const addFilters = useCallback(newFilters => dispatch(addFiltersAction(newFilters, (a, b) => {
    if (a.filterSource === FilterOptionSource.CURRENT_USER && b.filterSource !== FilterOptionSource.CURRENT_USER) {
      return -1;
    }
    if (a.filterSource !== FilterOptionSource.CURRENT_USER && b.filterSource === FilterOptionSource.CURRENT_USER) {
      return 1;
    }
    return 0;
  })), [dispatch]);
  const updateFilter = useCallback((id, isChecked) => dispatch(updateFilterState(id, isChecked, true)), [dispatch]);
  const clearFilter = useCallback(() => {
    filters.forEach(f => dispatch(updateFilterState(f.id, false)));
  }, [filters, dispatch]);
  const resetFilter = useCallback(() => {
    dispatch(replaceAllFiltersAction(defaultFilters));
  }, [defaultFilters, dispatch]);
  return {
    availableFilters: filters,
    addFilters,
    updateFilter,
    clearFilter,
    reset: resetFilter,
    dispatch
  };
};
const DEFAULT_FILTER_STATE = {
  availableFilters: [],
  addFilters: () => {},
  updateFilter: () => {},
  clearFilter: () => {},
  reset: () => {},
  dispatch: () => {}
};
const FilterContext = /*#__PURE__*/createContext({
  projectFilters: DEFAULT_FILTER_STATE,
  assigneeFilters: DEFAULT_FILTER_STATE,
  siteFilters: DEFAULT_FILTER_STATE,
  binaryStatusCategoryFilters: DEFAULT_FILTER_STATE,
  dateFilterFrom: {
    value: '',
    setValue: _value => null
  },
  dateFilterTo: {
    value: '',
    setValue: _value => null
  }
});
export function transformFiltersForAnalytics(availableFilters) {
  const appliedFilters = availableFilters.map((availableFilter, index) => ({
    ...availableFilter,
    index
  })).filter(filterWithIndex => filterWithIndex.isChecked);
  return {
    applied: appliedFilters.map(appliedFilter => ({
      id: appliedFilter.id,
      source: appliedFilter.filterSource,
      index: appliedFilter.index
    })),
    recommendedIds: availableFilters.map(appliedFilter => ({
      id: appliedFilter.id,
      source: appliedFilter.filterSource
    }))
  };
}
const transformDateFiltersForAnalytics = dateFilterFrom => {
  const availiableFilters = BROCCOLI_EXPERIMENTS.map(experiment => experiment.days).map(days => [days, getDateXDaysBack(days)]);
  const appliedFilter = availiableFilters.filter(([_, date]) => date === dateFilterFrom).map(([days, _]) => ({
    id: days
  }));
  return {
    applied: appliedFilter.map(({
      id
    }) => ({
      id,
      source: FilterOptionSource.STATIC
    })),
    recommendedIds: availiableFilters.map(([days, _]) => ({
      id: days,
      source: FilterOptionSource.STATIC
    }))
  };
};
export const FilterContextProvider = ({
  isEnabled = true,
  defaultProjectFilters,
  defaultAssigneeFilters,
  defaultSiteFilters,
  children
}) => {
  const previousEnabledState = React.useRef(isEnabled);
  const [dateFilterFrom, setDateFilterFrom] = useState('');
  const [dateFilterTo, setDateFilterTo] = useState('');
  const projectFilterStore = useFilters(defaultProjectFilters);
  const assigneeFilterStore = useFilters(defaultAssigneeFilters);
  const siteFilterStore = useFilters(defaultSiteFilters);
  const binaryStatusCategoryFilterStore = useFilters(DEFAULT_BINARY_STATUS_CATEGORY_FILTERS);
  const filtersAnalyticsContext = projectFilterStore.availableFilters.length === 0 && assigneeFilterStore.availableFilters.length === 0 ? {} : {
    projects: transformFiltersForAnalytics(projectFilterStore.availableFilters),
    assignees: transformFiltersForAnalytics(assigneeFilterStore.availableFilters),
    binaryStatusCategories: transformFiltersForAnalytics(binaryStatusCategoryFilterStore.availableFilters),
    dateFrom: transformDateFiltersForAnalytics(dateFilterFrom)
  };

  // We reset the state when the component becomes disabled
  if (previousEnabledState.current && !isEnabled) {
    projectFilterStore.reset();
    assigneeFilterStore.reset();
    siteFilterStore.reset();
    binaryStatusCategoryFilterStore.clearFilter();
    setDateFilterFrom('');
    setDateFilterTo('');
  }
  previousEnabledState.current = isEnabled;
  return /*#__PURE__*/React.createElement(FilterContext.Provider, {
    value: {
      projectFilters: projectFilterStore,
      assigneeFilters: assigneeFilterStore,
      siteFilters: siteFilterStore,
      binaryStatusCategoryFilters: binaryStatusCategoryFilterStore,
      dateFilterFrom: {
        value: dateFilterFrom,
        setValue: setDateFilterFrom
      },
      dateFilterTo: {
        value: dateFilterTo,
        setValue: setDateFilterTo
      }
    }
  }, /*#__PURE__*/React.createElement(SearchDialogAnalyticsContext, {
    analyticContext: {},
    nonPrivacySafeAnalyticContext: {},
    filterAnalyticsContext: filtersAnalyticsContext
  }, children));
};
export const useFilterContext = () => {
  const filterContext = React.useContext(FilterContext);
  return filterContext;
};